<template>
    <div class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">Imagens</p>
        </div>

        <div class="muven-content">
            <Loading page v-if="loading" />        

            <div class="mb-3" v-if="!loading">
                <div>
                    <button type="button" 
                        class="btn btn-primary"
                        @click="showImageFormModal()">
                        Adicionar imagens
                    </button>
                </div>
            </div>

            <div class="modal" tabindex="-1" role="dialog" id="muven-image-list-form-modal">
                <div class="modal-dialog" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Armazenamento de imagens</h5>
                        </div>
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-12">
                                    <div class="muven-tip tip-info mb-3">
                                        <fa icon="info" class="tip-icon" />
                                        <p>O tamanho máximo das imagens aceito é de 5MB.</p>
                                        <p>As extensões aceitas são: JPG, JPEG ou PNG</p>
                                    </div>
                                </div>

                                <div class="col-12 form-group">
                                    <label>Escolha as imagens</label>
                                    <input type="file" 
                                        class="form-control" 
                                        multiple
                                        accept=".jpg,.jpeg,.png"
                                        @change="filesChosenHandler" />
                                </div>

                                <div class="col-12 form-group">
                                    <label>URL da imagem (opcional)</label>
                                    <input type="text" 
                                        class="form-control"
                                        v-model="imageUrl"/>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-primary" 
                                @click="createImageList(chosenFiles, imageUrl)"
                                :disabled="!hasImages || submittingImageCreation">
                                    Adicionar
                                    <Loading v-show="submittingImageCreation" />
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeImageFormModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <NothingFound v-if="nothingToShow" />

            <div v-if="!loading && !nothingToShow">
                <div class="table-responsive">
                    <table class="table table-sm table-hover table-striped">
                        <thead>
                            <tr>
                                <th>Miniatura</th>
                                <th>Url</th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="(image, index) in imageList" :key="index">
                                <td class="image-thumbnail-container">
                                    <img :src="image.url" />
                                </td>
                                <td class="image-link-container">
                                    <a :href="image.url" target="_blank">{{image.url}}</a>
                                </td>                                
                                <td class="text-center muven-td-with-buttons image-action-container">
                                    <button type="button" 
                                        class="btn btn-clipboard-js text-muted" 
                                        @click="imageUrlCopiedHandler(image)"
                                        :data-clipboard-text="image.url">
                                        <span v-show="!image.copied">Copiar URL</span>
                                        <span v-show="image.copied">Copiado <fa icon="check-circle" /> </span>
                                    </button>                                    
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <ListCount :amount="imageList.length" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import productImageService from "@/product/services/ProductImageService";
import modalService from "@/utils/services/ModalService";
import { fileToBase64 } from "@/utils/helpers/FileHelper";
import ClipboardJS from "clipboard";

const ID_MODAL_FORM_IMAGE = "#muven-image-list-form-modal";

function _validImageUrl(imageUrl) {
    return !!imageUrl && /^http/.test(imageUrl)
}

function _transformImageList(imageList) {
    return imageList.map((url) => {
        return {
            url,
            copied: false,
        }
    })
}

export default {
    name: 'ProductImageListView',
    data() {
        return {
            loading: false,
            submittingImageCreation: false,
            clipboard: new ClipboardJS('.btn.btn-clipboard-js'),
            chosenFiles: [],
            imageList: [],
            imageUrl: "",
        }
    },
    async mounted() {
        this.loading = true;

        try {
            this.imageList = _transformImageList(await productImageService.getImageList());
        } catch (e) {
            this.$floatingAlert.error({
                message: e.message,
            });
        }

        this.loading = false;
    },
    methods: {
        async filesChosenHandler({target}) {
            for (const file of target.files) {
                const fileText = await fileToBase64(file);                
                const fileTextSplit = fileText.split(";base64,");
                this.chosenFiles.push(fileTextSplit[1]);
            }
        },
        imageUrlCopiedHandler(img) {
            img.copied = true;

            setTimeout(() => {
                img.copied = false;
            }, 2000);
        },
        async showImageFormModal() {
            this.imageUrl = "";
            this.chosenFiles = [];
            await modalService.show(ID_MODAL_FORM_IMAGE);
        },
        async closeImageFormModal() {
            await modalService.hide(ID_MODAL_FORM_IMAGE);
        },
        async createImageList(images, imageUrl) {
            this.submittingImageCreation = true;

            if (_validImageUrl(imageUrl)) {
                images.push(imageUrl);
            }

            try {
                this.imageList = _transformImageList(await productImageService.createImageList(images));

                this.closeImageFormModal();
                this._resetForm();
                
                this.$floatingAlert.success({
                    message: "Imagem armazenada com sucesso!",
                });
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                });
            }

            this.submittingImageCreation = false;
        },
        _resetForm() {
            document.querySelector(`${ID_MODAL_FORM_IMAGE} input[type="file"]`).value = null;
            this.chosenFiles = [];
        }
    },
    computed: {
        nothingToShow() {
            return !this.imageList.length && !this.loading;
        },
        hasImages() {
            return !!this.chosenFiles.length || _validImageUrl(this.imageUrl);
        },
    },
}
</script>

<style lang="scss" scoped>
.table {
    .image-thumbnail-container {
        img {
            max-height: 75px;
            width: auto;
        }
    }

    .image-link-container {
        font-size: .9em;
        padding-top: 30px;
    }

    .image-action-container {
        width: 150px;
    }
}
</style>